import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import { Image } from '../../widgets/Image';

const confidenceImg = require('../../assets/images/illustration/confidence.svg');
const designImg = require('../../assets/images/illustration/design.svg');
const designToolsImg = require('../../assets/images/logo-illustration/design-tools.svg');
const frontEndToolsImg = require('../../assets/images/icon-illustration/front-end-tools.svg');
const rapidImg = require('../../assets/images/icon-illustration/rapid-prototyping-design.svg');
const uiDesignImg = require('../../assets/images/icon-illustration/ui-design.svg');
const enterpriseUIDesignImg = require('../../assets/images/icon-illustration/enterprise-app-ui-design.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');
const userResearchImg = require('../../assets/images/icon-illustration/user-research.svg');

import * as css from './RapidPrototyping.m.css';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import WhySitePen from '../../widgets/WhySitePen';

const factory = create();

export const RapidPrototyping = factory(function RapidPrototyping() {
	return (
		<div classes={css.root}>
			<Head
				title="Rapid Prototyping & Software Design | SitePen"
				description="SitePen deploys rapid prototyping and design in our software development process. We thrive on quickly iterating to create the best possible product for our customers."
			/>

			<Hero background="level3Green">
				{{
					xlTitle: (
						<div>
							Rapid
							<br />
							Prototyping
						</div>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse paddingBottom="md">
						{{
							title: 'Iterate to Perfection',
							blurb:
								'In the software development life-cycle, rapid prototyping can be an invaluable tool that allows for quick iteration early in the UI design process. It is also an essential tool for evaluating additions and revisions to an existing product. By exploring concepts in a less refined form, the inefficiencies and inadequacies become more readily apparent. This saves time by allowing only the best concepts to be explored fully. With the proper rapid ui prototyping tools and the help of experienced UI / UX experts, you can utilize rapid prototyping to build or expand your product efficiently and effectively.',
							image: <Image src={designImg} size="md" />
						}}
					</Summary>
					<WhySitePen />
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'What is Rapid Prototyping in Software Testing?',
							blurb: (
								<virtual>
									Rapid prototyping UI design is a part of the design process that
									allows for quick exploration of a proposed feature /
									improvements to your software. Prototyping low to medium
									fidelity mockups allow for early feedback from stakeholders and
									users. These prototypes often include interactivity. Higher
									fidelity prototypes can also be utilized later in the process to
									gather additional feedback and for communicating requirements to
									developers.
								</virtual>
							),
							items: [
								{
									title: 'Competitive Analysis',
									blurb:
										'In the early stages of your project, competitive research and analysis is an integral part of the software development process. As competitor research is conducted and unique selling points are established, it becomes important to explore the efficacy of your proposed direction. Rapid prototyping UI design helps to allow for quick exploration of core features and key differentiators. This allows for much-needed clarity as you evaluate the prospective features of your product.',
									image: (
										<Image
											src={enterpriseUIDesignImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'MVP Development',
									blurb:
										'Rapid prototyping UI design is an indispensable tool when establishing a minimum viable product. As you develop the vision and direction of your product, it is important to work efficiently and keep nonessential details to a minimum. Rapid prototyping helps streamline the design process and allows key features to be explored and presented to stakeholders. Any unnecessary feature or flourish will naturally be discarded during prototyping in order to champion the MVP.',
									image: (
										<Image
											src={frontEndToolsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Interactive Prototypes',
									blurb:
										'While static mockups offer plenty of utility, interactive prototypes allow for a deeper level of analysis. Designers, stakeholders, and user testers can click through the prototype to better evaluate the flow of the app. It also helps them understand the context surrounding a feature. Additionally, interactive prototypes help developers understand the expected functionality of the UI. Most rapid ui prototyping tools (such as Figma or Sketch) have interactive prototyping built in. Static mockups can also be converted into interactive prototypes with apps such as InVision',
									image: <Image src={rapidImg} lazy size="icon" align="center" />
								},
								{
									title: 'Usability Testing',
									blurb:
										'Rapid Prototyping allows you to get your product in front of testers early and often. Interactive prototypes allow the user to click through the prospective interface as they would in your final application. This allows their interactions to be studied and allows them to provide direct feedback. By quickly iterating on user feedback, your application can avoid costly rabbit holes that don’t provide value to your end product.',
									image: (
										<Image
											src={userResearchImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Software Prototyping Services',
									blurb:
										'Often Rapid Prototyping can fall short of your goals if you’re not leveraging vetted UI | UX experts. At SitePen, we have a team of designers that are not only well-versed in leading-edge tools and methodology, but are also grounded by years of experience in user-centered design. We provide comprehensive solutions that meet user expectations while also achieving business objectives.',
									image: (
										<Image src={uiDesignImg} lazy size="icon" align="center" />
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse stackedPadding={true}>
						{{
							title: 'Technologies We Use',
							blurb:
								'SitePen utilizes the best leading-edge technology for our design process. We have in-depth experience with industry standard applications such as Sketch, Figma, and the Adobe Suite. Our abilities are not limited to mockups and static screenshots, but also incorporate clickable prototypes built with InVision, Framer, Axure, and Figma. We leverage existing design libraries and design systems such as Google’s Material Design and Bootstrap, and we are experts at developing custom systems ourselves. Whatever the need, we have experience with the right tools and technology to deliver fast and beautiful results for your project.',
							image: <Image src={designToolsImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={confidenceImg} lazy size="lg" />,
							title: 'Our Rapid Prototyping & Design Process',
							blurb: (
								<virtual>
									<p>
										In a typical project we start out with a discovery phase
										followed by business analysis. Once the objectives of a
										project are defined, the UI / UX design process starts.
										During this phase we leverage rapid prototyping to establish
										the core features of the application. Throughout the design
										phase we iterate on the proposed designs to help them align
										with user expectation, business requirements, and technical
										limitations. Once complete, the designs can then be released
										for development.
									</p>
									<p>
										While rapid prototyping is usually a part of a dedicated
										design phase, it is often utilized earlier in the discovery
										phase to exhibit a proof of concept. It can also be utilized
										later in the process to adjust existing features or
										troubleshoot user experience issues.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<IndustryExpertise />
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={teamEfficiencyImg} lazy size="lg" />,
							title: 'Choosing the Right Software Development Company',
							blurb: (
								<virtual>
									Every project has unique challenges and demands. It is therefore
									important to avoid “one size fits all” solutions that don’t
									adapt to the needs of your product. At SitePen we take the time
									to understand your business objectives. We work with you to
									craft a solution that fully realizes those objectives. Don’t
									have your objectives fully fleshed out? SitePen can provide you
									with the business analysis needed to define your product more
									clearly and prioritize objectives.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="Rapid Prototyping FAQs">
					{[
						{
							title: 'What Tools Are Used in Rapid Prototyping?',
							content: (
								<p>
									Figma is the most common rapid ui prototyping tool, followed by
									Sketch. Figma has fairly advanced built-in prototyping tools,
									while Sketch’s prototyping tools are less fully featured.
									Sometimes Sketch is paired with InVision to turn static mockups
									into more flexible, shareable prototypes. Axure in another
									commonly used tool when the sole focus is low-fidelity rapid
									prototyping.
								</p>
							)
						},
						{
							title: 'How Does Rapid Prototyping Improve UX?',
							content: (
								<p>
									Rapid prototyping improves the user experience by allowing for
									quick iteration and early feedback from users and stakeholders.
									Once feedback has been analyzed, it allows further refinement to
									the application. This helps to weed out unproductive rabbit
									holes, unnecessary features, and user experience problems.
								</p>
							)
						},
						{
							title: 'How Long Does the Rapid Prototyping Process Take?',
							content: (
								<p>
									Rapid Prototyping, as the name would suggest, is a more
									expedient step within the design process. Rapid prototyping for
									a single feature within an app can take as little as a day or
									two. However, often, an array of features are designed together
									over several weeks or months – depending on the size of the
									project. The length of the rapid prototyping phase will
									ultimately depend on the project objectives.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default RapidPrototyping;
